import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import RevenueGraphic from './RevenueGraphic.tsx';
import {
    Box,
    Container,
    Typography,
    Button,
    GlobalStyles,
    CssBaseline,
} from '@mui/material';
import { createTheme, ThemeProvider, alpha } from '@mui/material/styles';
import { RocketLaunch, KeyboardArrowDown } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const BRAND = {
    purple:      '#7B5EA7',
    purpleLight: '#9B7EC8',
    purpleDark:  '#3D2A6B',
    purpleDeep:  '#1A0F3D',
    lavenderSoft:'#F7F4FC',
    accent:      '#C8B8E8',
    ink:         '#0D0A1A',
    cream:       '#FAF9FC',
} as const;

const theme = createTheme({
    palette: {
        primary:   { main: BRAND.purple, light: BRAND.purpleLight, dark: BRAND.purpleDark },
        background:{ default: BRAND.cream, paper: '#fff' },
        text:      { primary: BRAND.ink, secondary: '#5A5070' },
    },
    /**
    typography: {
        fontFamily: "'DM Sans', system-ui, sans-serif",
        h1: { fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400 },
        h2: { fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400 },
        h3: { fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400 },
        h4: { fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400 },
    }, **/
    typography: {
        // Swapped to a premium geometric tech font stack
        fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
        h1: { fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontWeight: 600, letterSpacing: '-0.03em' },
        h2: { fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontWeight: 500, letterSpacing: '-0.02em' },
        h3: { fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontWeight: 500, letterSpacing: '-0.02em' },
        h4: { fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontWeight: 400, letterSpacing: '-0.01em' },
    },
    shape: { borderRadius: 16 },
    components: {
        MuiButton: {
            styleOverrides: {
                root: { borderRadius: 100, textTransform: 'none', fontWeight: 700, letterSpacing: 0 },
            },
        },
    },
});

const AppGlobalStyles: React.FC = () => (
    <GlobalStyles styles={`
    @import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:wght@300;400;500;600;700&display=swap');
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    html { scroll-behavior: smooth; }
    body { overflow-x: hidden; width: 100%; margin: 0; padding: 0; }

    .reveal        { opacity:0; transform:translateY(44px); transition:opacity .9s cubic-bezier(.16,1,.3,1), transform .9s cubic-bezier(.16,1,.3,1); }
    .reveal-left   { opacity:0; transform:translateX(-52px); transition:opacity .9s cubic-bezier(.16,1,.3,1), transform .9s cubic-bezier(.16,1,.3,1); }
    .reveal-right  { opacity:0; transform:translateX(52px);  transition:opacity .9s cubic-bezier(.16,1,.3,1), transform .9s cubic-bezier(.16,1,.3,1); }
    .reveal.visible, .reveal-left.visible, .reveal-right.visible { opacity:1; transform:none; }
    .d1 { transition-delay:.1s!important; } .d2 { transition-delay:.2s!important; }
    .d3 { transition-delay:.3s!important; } .d4 { transition-delay:.4s!important; }

    @keyframes marquee { from{transform:translateX(0)} to{transform:translateX(-50%)} }
    .marquee-track { display:flex; width:max-content; animation:marquee 30s linear infinite; }
    .marquee-track:hover { animation-play-state:paused; }

    @keyframes heroFadeUp { from{opacity:0;transform:translateY(28px)} to{opacity:1;transform:none} }
  `} />
);

const useReveal = (): void => {
    useEffect(() => {
        const els = document.querySelectorAll<HTMLElement>('.reveal,.reveal-left,.reveal-right');
        const io = new IntersectionObserver(
            entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
            { threshold: 0.1 }
        );
        els.forEach(el => io.observe(el));
        return () => io.disconnect();
    }, []);
};

const useCountUp = (target: number, duration = 1800): [number, React.RefObject<HTMLDivElement>] => {
    const [val, setVal] = useState(0);
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const io = new IntersectionObserver(([entry]) => {
            if (!entry.isIntersecting) return;
            io.disconnect();
            const start = performance.now();
            const step = (now: number) => {
                const t = Math.min((now - start) / duration, 1);
                const e = t < .5 ? 2*t*t : -1+(4-2*t)*t;
                setVal(Math.round(e * target));
                if (t < 1) requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
        }, { threshold: 0.5 });
        io.observe(el);
        return () => io.disconnect();
    }, [target, duration]);
    return [val, ref];
};

const Logo: React.FC<{ size?: number }> = ({ size = 36 }) => (
    <Box
        component="img"
        src="images/ngc.png"
        alt="NG Credit"
        sx={{ width: size, height: size, borderRadius: `${size * 0.18}px`, display: 'block', flexShrink: 0 }}
    />
);

const Nav: React.FC = () => {
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const fn = () => setScrolled(window.scrollY > 60);
        window.addEventListener('scroll', fn);
        return () => window.removeEventListener('scroll', fn);
    }, []);

    const navLinks = (['Platform', 'Architecture', 'Ecosystem', 'Revenue'] as const);

    return (
        <>
            <Box
                component="nav"
                sx={{
                    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
                    px: { xs: 3, md: 6 }, py: 2.5,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    transition: 'background .4s, backdrop-filter .4s',
                    background: scrolled || mobileOpen ? alpha(BRAND.purpleDeep, 0.96) : 'transparent',
                    backdropFilter: scrolled || mobileOpen ? 'blur(18px)' : 'none',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Logo size={34} />
                    <Typography sx={{ fontFamily: "'Instrument Serif',serif", fontSize: '1.35rem', color: '#fff', letterSpacing: '-.01em' }}>
                        NG Credit
                    </Typography>
                </Box>

                <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 3.5 }}>
                    {navLinks.map(l => (
                        <Box key={l} component="a" href={`#${l.toLowerCase()}`} sx={{
                            color: 'rgba(255,255,255,.7)', fontSize: '.84rem', textDecoration: 'none',
                            fontWeight: 500, letterSpacing: '.02em', transition: 'color .2s',
                            '&:hover': { color: BRAND.accent },
                        }}>{l}</Box>
                    ))}
                    <Button variant="outlined" size="small" onClick={() => window.open('/demo')}
                            sx={{ borderColor: 'rgba(255,255,255,.3)', color: '#fff', px: 2.5, py: 1, fontSize: '.82rem', '&:hover': { borderColor: 'rgba(255,255,255,.7)', background: 'rgba(255,255,255,.07)' }, transition: 'all .25s' }}>
                        Platform Demo
                    </Button>
                    <Button variant="contained" size="small" startIcon={<RocketLaunch sx={{ fontSize: '.9rem!important' }} />} onClick={() => navigate('/apply')}
                            sx={{ background: BRAND.purple, px: 2.5, py: 1, fontSize: '.82rem', '&:hover': { background: BRAND.purpleLight, transform: 'translateY(-2px)', boxShadow: `0 8px 24px ${alpha(BRAND.purple,.45)}` }, transition: 'all .25s' }}>
                        Deploy Credit
                    </Button>
                </Box>

                <Box sx={{ display: { xs: 'flex', md: 'none' }, cursor: 'pointer', p: 1, borderRadius: 2, '&:hover': { background: alpha('#fff', .08) }, transition: 'background .2s' }}
                     onClick={() => setMobileOpen(o => !o)}>
                    <Box sx={{ position: 'relative', width: 24, height: 24 }}>
                        {/* Top bar */}
                        <Box sx={{ position: 'absolute', top: mobileOpen ? '11px' : '4px', left: 0, right: 0, height: 2, background: '#fff', borderRadius: 2, transformOrigin: 'center', transform: mobileOpen ? 'rotate(45deg)' : 'none', transition: 'top .3s, transform .3s' }} />
                        {/* Mid bar */}
                        <Box sx={{ position: 'absolute', top: '11px', left: 0, right: 0, height: 2, background: '#fff', borderRadius: 2, opacity: mobileOpen ? 0 : 1, transition: 'opacity .2s' }} />
                        {/* Bottom bar */}
                        <Box sx={{ position: 'absolute', top: mobileOpen ? '11px' : '18px', left: 0, right: 0, height: 2, background: '#fff', borderRadius: 2, transformOrigin: 'center', transform: mobileOpen ? 'rotate(-45deg)' : 'none', transition: 'top .3s, transform .3s' }} />
                    </Box>
                </Box>
            </Box>

            <Box sx={{
                position: 'fixed', top: 0, left: 0, right: 0, zIndex: 99,
                background: alpha(BRAND.purpleDeep, 0.98), backdropFilter: 'blur(20px)',
                pt: '80px', pb: 4, px: 3, minHeight: '100vh',
                transform: mobileOpen ? 'translateY(0)' : 'translateY(-110%)',
                transition: 'transform .4s cubic-bezier(.16,1,.3,1)',
                display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 0,
                boxShadow: `0 32px 60px ${alpha(BRAND.purpleDeep, .6)}`,
            }}>
                {navLinks.map(l => (
                    <Box key={l} component="a" href={`#${l.toLowerCase()}`} onClick={() => setMobileOpen(false)}
                         sx={{ color: 'rgba(255,255,255,.75)', fontSize: '1.1rem', textDecoration: 'none', fontWeight: 500, py: 1.75, px: 1, borderBottom: `1px solid ${alpha('#fff', .08)}`, transition: 'color .2s, padding-left .2s', '&:hover': { color: '#fff', pl: 2 } }}>
                        {l}
                    </Box>
                ))}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
                    <Button variant="outlined" fullWidth onClick={() => { window.open('/demo'); setMobileOpen(false); }}
                            sx={{ borderColor: 'rgba(255,255,255,.3)', color: '#fff', py: 1.5, fontSize: '.95rem', '&:hover': { borderColor: 'rgba(255,255,255,.7)', background: 'rgba(255,255,255,.07)' } }}>
                        Platform Demo
                    </Button>
                    <Button variant="contained" fullWidth startIcon={<RocketLaunch />} onClick={() => { navigate('/apply'); setMobileOpen(false); }}
                            sx={{ background: BRAND.purple, py: 1.5, fontSize: '.95rem', '&:hover': { background: BRAND.purpleLight } }}>
                        Deploy Credit
                    </Button>
                </Box>
            </Box>
        </>
    );
};

const Hero: React.FC = () => {
    const navigate = useNavigate();
    return (
        <Box id="platform" sx={{
            position: 'relative', height: '100vh', minHeight: 680,
            display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
            background: `linear-gradient(145deg, ${BRAND.purpleDeep} 0%, ${BRAND.purpleDark} 55%, ${BRAND.purple} 100%)`,
        }}>
            <Box sx={{ position: 'absolute', inset: 0, backgroundImage: 'url(https://images.unsplash.com/photo-1585911171167-1f66ea3de00c?auto=format&fit=crop&q=80&w=1800)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.1, mixBlendMode: 'luminosity' }} />
            <Box sx={{ position: 'absolute', inset: 0, background: `radial-gradient(ellipse at 50% 60%, transparent 0%, ${alpha(BRAND.purpleDeep,.8)} 75%)` }} />
            <Box sx={{ position: 'absolute', top: '8%', right: '6%', width: { xs: 240, md: 460 }, height: { xs: 240, md: 460 }, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(BRAND.purpleLight,.22)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
            <Box sx={{ position: 'absolute', bottom: '12%', left: '4%', width: { xs: 160, md: 320 }, height: { xs: 160, md: 320 }, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(BRAND.accent,.14)} 0%, transparent 70%)`, pointerEvents: 'none' }} />

            <Container maxWidth="md" sx={{ position: 'relative', textAlign: 'center', px: { xs: 3, md: 4 } }}>
                <Typography sx={{ color: BRAND.accent, fontSize: '.75rem', letterSpacing: '.2em', fontWeight: 600, textTransform: 'uppercase', mb: 3, animation: 'heroFadeUp .8s ease forwards' }}>
                    Nigeria's Industrial Credit Platform
                </Typography>
                <Typography variant="h1" sx={{ color: '#fff', fontSize: { xs: '2.8rem', sm: '4rem', md: '5.5rem', lg: '6.5rem' }, lineHeight: 1.05, mb: 3, letterSpacing: '-.025em', animation: 'heroFadeUp .9s .1s ease both' }}>
                    Finance the Tools<br />
                    <Box component="em" sx={{ fontStyle: 'italic', color: BRAND.accent }}>That Build Nigeria</Box>
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,.6)', fontSize: { xs: '1rem', md: '1.15rem' }, maxWidth: 520, mx: 'auto', mb: 4.5, lineHeight: 1.75, animation: 'heroFadeUp .9s .2s ease both' }}>
                    NG Credit deploys industrial capacity — not consumer debt. Powered by verified financial data and our proprietary scoring model, we finance the assets that produce food, energy, and industrial materials.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', animation: 'heroFadeUp .9s .3s ease both' }}>
                    <Button variant="outlined" size="large" onClick={() => window.open('/demo')}
                            sx={{ borderColor: 'rgba(255,255,255,.3)', color: '#fff', px: 4.5, py: 1.8, fontSize: '.95rem', '&:hover': { borderColor: 'rgba(255,255,255,.7)', background: 'rgba(255,255,255,.07)' } }}>
                        View Platform Demo
                    </Button>
                </Box>
            </Container>

            <Box sx={{ position: 'absolute', bottom: 40, left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: .5, animation: 'heroFadeUp 1s .8s ease both' }}>
                <Typography sx={{ color: 'rgba(255,255,255,.3)', fontSize: '.65rem', letterSpacing: '.2em', textTransform: 'uppercase' }}>Scroll</Typography>
                <KeyboardArrowDown sx={{ color: 'rgba(255,255,255,.3)', fontSize: '1.2rem' }} />
            </Box>
        </Box>
    );
};

const MARQUEE_ITEMS = ['Pilot Assets Active','✦','Greenhouses & Ponds','✦','Eko Bricks Collection Hubs','✦','Circular Industrial Model','✦','Repayment Rate: 100%','✦'];

const Marquee: React.FC = () => (
    <Box sx={{ background: BRAND.purple, py: 1.6, overflow: 'hidden' }}>
        <Box className="marquee-track">
            {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((s, i) => (
                <Typography key={i} sx={{ whiteSpace: 'nowrap', px: 3.5, fontSize: '.78rem', fontWeight: 600, letterSpacing: '.06em', color: '#fff', textTransform: 'uppercase', opacity: s === '✦' ? .4 : .9 }}>
                    {s}
                </Typography>
            ))}
        </Box>
    </Box>
);

const ABOUT_STATS = [
    { value: '₦100M+', label: 'Assets Financed (Pilot)' },
    { value: '3',      label: 'Active SME Partners' },
    { value: '3',      label: 'Production Sites' },
    { value: '100%',   label: 'Repayment Rate' },
];

const About: React.FC = () => (
    <Box sx={{ background: BRAND.purpleDeep, py: { xs: 10, md: 16 }, px: { xs: 3, md: 6 } }}>
        <Container maxWidth="lg">
            <Typography className="reveal" sx={{ color: BRAND.accent, fontSize: '.75rem', letterSpacing: '.2em', textTransform: 'uppercase', fontWeight: 600, mb: 4 }}>About NG Credit</Typography>
            <Typography variant="h2" className="reveal" sx={{ color: '#fff', fontSize: { xs: '1.9rem', md: '2.8rem', lg: '3.5rem' }, lineHeight: 1.2, maxWidth: 840, mb: 8 }}>
                We transform idle capital into productive industrial assets — greenhouses, fish ponds, and plastic processing centers — that make Nigeria food sovereign and construction-ready.
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: '1px', background: alpha('#fff',.08), borderRadius: 3, overflow: 'hidden' }}>
                {ABOUT_STATS.map((s, i) => (
                    <Box key={i} className={`reveal d${i+1}`} sx={{ background: BRAND.purpleDeep, p: { xs: 3, md: 5 } }}>
                        <Typography variant="h3" sx={{ color: BRAND.accent, fontSize: { xs: '2rem', md: '3rem' }, fontWeight: 400 }}>{s.value}</Typography>
                        <Typography sx={{ color: alpha('#fff',.45), fontSize: '.82rem', mt: 1, letterSpacing: '.04em' }}>{s.label}</Typography>
                    </Box>
                ))}
            </Box>
        </Container>
    </Box>
);

interface Layer { num: string; title: string; desc: string; img: string; }
const LAYERS: Layer[] = [
    { num: '01', title: 'Asset Layer', desc: 'NG Credit owns the productive hard assets — commercial greenhouses, drying and processing facilities, drip irrigation systems, and fish ponds — deployed through structured lease-to-own agreements. We bear the upfront CapEx so SMEs can focus on growing & processing foodstuff, not sourcing finance.', img: 'images/OLA_6001.jpg' },
    { num: '02', title: 'Credit Application Layer', desc: 'Our credit application layer evaluates and develops SME operators before productive assets are deployed. Users establish credit profiles, monitor eligibility, and receive actionable guidance to improve their financial standing. NG Credit creates a reliable pathway for entrepreneurs to access lease-to-own agreements for commercial greenhouses, processing facilities, irrigation systems, and other productive assets.', img: 'images/img.png' },
    { num: '03', title: 'Market Layer', desc: 'Before financing is deployed, off-take agreements with institutional buyers, supermarkets and food distributors are secured. Revenue is pre-committed — transforming credit risk into infrastructure risk, the kind we know how to manage and price.', img: 'images/market.jpg' },
];

const Architecture: React.FC = () => {
    const [active, setActive] = useState(0);
    return (
        <Box id="architecture" sx={{ background: BRAND.lavenderSoft, py: { xs: 10, md: 16 }, px: { xs: 3, md: 6 } }}>
            <Container maxWidth="lg">
                <Typography className="reveal" sx={{ color: BRAND.purple, fontSize: '.75rem', letterSpacing: '.2em', textTransform: 'uppercase', fontWeight: 600, mb: 2 }}>How It Works</Typography>
                <Typography variant="h2" className="reveal" sx={{ color: BRAND.purpleDark, fontSize: { xs: '2rem', md: '3rem' }, mb: 8, lineHeight: 1.15 }}>Three-Layer Architecture</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1.15fr' }, gap: { xs: 6, md: 10 }, alignItems: 'start' }}>
                    <Box>
                        {LAYERS.map((l, i) => (
                            <Box key={i} onClick={() => setActive(i)} sx={{ py: 3.5, cursor: 'pointer', borderTop: `1px solid ${i === active ? BRAND.purple : alpha(BRAND.purpleDark,.15)}`, transition: 'border-color .3s' }}>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mb: i === active ? 1.5 : 0 }}>
                                    <Typography sx={{ fontSize: '.7rem', color: BRAND.purple, fontWeight: 700, letterSpacing: '.12em' }}>{l.num}</Typography>
                                    <Typography variant="h4" sx={{ fontSize: i === active ? '1.55rem' : '1.2rem', color: i === active ? BRAND.purpleDark : alpha(BRAND.purpleDark,.35), transition: 'all .35s', lineHeight: 1.2 }}>{l.title}</Typography>
                                </Box>
                                {i === active && <Typography sx={{ color: 'text.secondary', fontSize: '.95rem', lineHeight: 1.8, ml: 4.5, mt: .5 }}>{l.desc}</Typography>}
                            </Box>
                        ))}
                        <Box sx={{ borderTop: `1px solid ${alpha(BRAND.purpleDark,.15)}` }} />
                    </Box>
                    <Box sx={{ position: { md: 'sticky' }, top: 120 }}>
                        <Box sx={{ borderRadius: 4, overflow: 'hidden', aspectRatio: '4/3', boxShadow: `0 24px 80px ${alpha(BRAND.purpleDark,.2)}` }}>
                            <Box component="img" key={active} src={LAYERS[active].img} alt={LAYERS[active].title} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'opacity .4s' }} />
                        </Box>
                        <Box sx={{ mt: -6, ml: 3, position: 'relative', display: 'inline-block', background: BRAND.purple, borderRadius: 3, px: 3, py: 2.5, boxShadow: `0 12px 40px ${alpha(BRAND.purpleDark,.4)}` }}>
                            <Typography sx={{ color: BRAND.accent, fontSize: '.65rem', letterSpacing: '.14em', textTransform: 'uppercase', fontWeight: 700 }}>Layer {active + 1} of 3</Typography>
                            <Typography variant="h4" sx={{ color: '#fff', fontSize: '1.05rem', mt: .5 }}>{LAYERS[active].title}</Typography>
                        </Box>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
};

interface RoadmapStatProps { target: number; prefix?: string; suffix?: string; label: string; delay?: number; }

const RoadmapStat: React.FC<RoadmapStatProps> = ({ target, prefix = '', suffix = '', label, delay = 0 }) => {
    const [val, ref] = useCountUp(target);
    return (
        <Box ref={ref} className="reveal" sx={{ p: { xs: 3, md: 5 }, borderTop: `1px solid ${alpha('#fff',.1)}`, transitionDelay: `${delay}s!important` }}>
            <Typography variant="h3" sx={{ color: '#fff', fontWeight: 400, fontSize: { xs: '2rem', md: '3rem' } }}>
                {prefix}{val.toLocaleString()}{suffix}
            </Typography>
            <Typography sx={{ color: alpha('#fff',.4), fontSize: '.82rem', mt: 1 }}>{label}</Typography>
        </Box>
    );
};

const Roadmap: React.FC = () => (
    <Box sx={{ background: BRAND.ink, py: { xs: 10, md: 16 }, px: { xs: 3, md: 6 } }}>
        <Container maxWidth="lg">
            <Typography className="reveal" sx={{ color: BRAND.accent, fontSize: '.75rem', letterSpacing: '.2em', textTransform: 'uppercase', fontWeight: 600, mb: 2 }}>Building a Sovereign Economy</Typography>
            <Typography variant="h2" className="reveal" sx={{ color: '#fff', fontSize: { xs: '2rem', md: '3rem' }, mb: 8 }}>NG Credit's Roadmap to 2030</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3,1fr)' }, gap: '1px', background: alpha('#fff',.06), borderRadius: 3, overflow: 'hidden' }}>
                <RoadmapStat target={5000} suffix=" T"    label="Target annual food production capacity" delay={0} />
                <RoadmapStat target={1000} suffix="+"     label="Jobs targeted across industrial SMEs" delay={.08} />
                <RoadmapStat target={10}   suffix=" sites" label="Production & processing hubs planned" delay={.16} />
                <RoadmapStat target={97}   suffix="%"     label="Target repayment rate" delay={.24} />
                <RoadmapStat target={5}    prefix="₦"    suffix="B+" label="Total assets targeted by 2030" delay={.32} />
                <RoadmapStat target={30}   suffix="%"     label="Projected increase in SME crop yields" delay={.4} />
            </Box>
        </Container>
    </Box>
);

interface Stream { title: string; desc: string; }
const STREAMS: Stream[] = [
    { title: 'Lease Margins',      desc: 'Fixed returns on owned hardware — greenhouses, ponds, and processing equipment — across all hubs.' },
    { title: 'Revenue Sharing',    desc: 'Percentage of production output from each financed agricultural and aquaculture asset.' },
    { title: 'Bricks & Materials', desc: 'Margins on Eko Bricks output sold to construction firms, municipalities, and housing projects.' },
    { title: 'Data Services',      desc: 'Monetizing supply chain and waste-stream analytics for institutional buyers and urban planners.' },
];

const RevenueModel: React.FC = () => (
    <Box id="revenue" sx={{ background: BRAND.cream, py: { xs: 10, md: 16 }, px: { xs: 3, md: 6 } }}>
        <Container maxWidth="lg">
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 6, md: 10 }, alignItems: 'center' }}>
                <Box>
                    <Typography className="reveal" sx={{ color: BRAND.purple, fontSize: '.75rem', letterSpacing: '.2em', textTransform: 'uppercase', fontWeight: 600, mb: 2 }}>Revenue Engine</Typography>
                    <Typography variant="h2" className="reveal" sx={{ color: BRAND.purpleDark, fontSize: { xs: '2rem', md: '3rem' }, lineHeight: 1.15, mb: 3 }}>
                        Not just a pipe.<br />
                        <Box component="em" sx={{ fontStyle: 'italic', color: BRAND.purple }}>An infrastructure company.</Box>
                    </Typography>
                    <Typography className="reveal" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 4, fontSize: '1rem' }}>
                        We don't simply fund production — we monetize the entire industrial pipeline through four compounding revenue streams aligned directly with SME success.
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                        {STREAMS.map((s, i) => (
                            <Box key={i} className={`reveal d${i+1}`} sx={{ background: '#fff', border: `1px solid ${alpha(BRAND.purple,.12)}`, borderRadius: 3, p: 3, transition: 'border-color .3s, transform .3s, box-shadow .3s', cursor: 'default', '&:hover': { borderColor: BRAND.purple, transform: 'translateY(-4px)', boxShadow: `0 12px 32px ${alpha(BRAND.purple,.15)}` } }}>
                                <Typography sx={{ fontWeight: 700, color: BRAND.purpleDark, mb: 1, fontSize: '.95rem' }}>{s.title}</Typography>
                                <Typography sx={{ fontSize: '.83rem', color: 'text.secondary', lineHeight: 1.65 }}>{s.desc}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
                <Box className="reveal-right" sx={{ position: 'relative' }}>
                    <RevenueGraphic />
                </Box>
            </Box>
        </Container>
    </Box>
);

interface Feature { title: string; desc: string; }
const FEATURES: Feature[] = [
    { title: 'Agricultural & Aquaculture Assets',  desc: 'We finance greenhouses, drip irrigation systems, and commercial fish ponds — giving SMEs access to productive infrastructure without the burden of upfront capital.' },
    { title: 'Circular Waste-to-Materials Chain',  desc: 'Eko Bricks collection centers and grinding hubs mine plastic from land, lagoons, and waterways — converting urban waste into construction-grade materials that reduce imports and clean cities simultaneously.' },
    { title: 'Self-Sufficient Production Hubs',    desc: 'Our production sites are designed for decentralized independence — generating their own inputs and reducing reliance on unstable national infrastructure.' },
];

interface Partner { name: string; type: string; img: string; }
const PARTNERS: Partner[] = [
    { name: 'Greenhouse & Pond Operators', type: 'Agro-Production Partners',    img: 'images/OLA_5947.jpg' },
    { name: 'Eko Bricks Network',          type: 'Circular Materials Vertical', img: 'images/Bricks2.1.jpg' },
];

const Ecosystem: React.FC = () => (
    <Box id="ecosystem" sx={{ background: BRAND.purpleDark, py: { xs: 10, md: 16 }, px: { xs: 3, md: 6 } }}>
        <Container maxWidth="lg">
            <Typography className="reveal" sx={{ color: BRAND.accent, fontSize: '.75rem', letterSpacing: '.2em', textTransform: 'uppercase', fontWeight: 600, mb: 2 }}>Ecosystem</Typography>
            <Typography variant="h2" className="reveal" sx={{ color: '#fff', fontSize: { xs: '2rem', md: '3rem' }, mb: 8 }}>Two Verticals. One Industrial Stack.</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3,1fr)' }, gap: 4, mb: 8 }}>
                {FEATURES.map((f, i) => (
                    <Box key={i} className={`reveal d${i+1}`} sx={{ pt: 4, borderTop: `1px solid ${alpha('#fff',.15)}` }}>
                        <Typography variant="h4" sx={{ color: '#fff', fontSize: '1.2rem', fontWeight: 400, mb: 1.5 }}>{f.title}</Typography>
                        <Typography sx={{ color: alpha('#fff',.5), fontSize: '.88rem', lineHeight: 1.8 }}>{f.desc}</Typography>
                    </Box>
                ))}
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                {PARTNERS.map((p, i) => (
                    <Box key={i} className={`reveal d${i+1}`} sx={{ borderRadius: 4, overflow: 'hidden', position: 'relative', aspectRatio: '16/9', '&:hover img': { transform: 'scale(1.04)' } }}>
                        <Box component="img" src={p.img} alt={p.name} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform .6s ease' }} />
                        <Box sx={{ position: 'absolute', inset: 0, background: `linear-gradient(to top, ${alpha(BRAND.purpleDeep,.85)} 0%, transparent 55%)` }} />
                        <Box sx={{ position: 'absolute', bottom: 24, left: 24 }}>
                            <Typography variant="h4" sx={{ color: '#fff', fontSize: '1.15rem', fontWeight: 400 }}>{p.name}</Typography>
                            <Typography sx={{ color: BRAND.accent, fontSize: '.7rem', letterSpacing: '.12em', textTransform: 'uppercase', fontWeight: 600 }}>{p.type}</Typography>
                        </Box>
                    </Box>
                ))}
            </Box>
        </Container>
    </Box>
);

const CREDIT_FEATURES = [
    { num: '01', title: 'Instant Credit Score', desc: 'Get your NG Credit score in seconds — powered by real open banking data across income stability, expense ratio, repayment history, and account profile. No guesswork, no delays.' },
    { num: '02', title: 'Lease & Rent Readiness', desc: "Landlords and leasing companies increasingly require credit verification. Generate a shareable credit report that shows you're a reliable tenant or lessee — before they even ask." },
    { num: '03', title: 'Score Breakdown & Improvement', desc: "See exactly what's driving your score across six categories. Get actionable steps to improve your standing, set savings goals, and track progress month by month." },
];

const CreditCheck: React.FC = () => {
    const navigate = useNavigate();
    return (
        <Box sx={{ background: BRAND.lavenderSoft, py: { xs: 10, md: 16 }, px: { xs: 3, md: 6 } }}>
            <Container maxWidth="lg">
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 6, md: 12 }, alignItems: 'center' }}>
                    <Box>
                        <Typography className="reveal" sx={{ color: BRAND.purple, fontSize: '.75rem', letterSpacing: '.2em', textTransform: 'uppercase', fontWeight: 600, mb: 2 }}>For Everyday Nigerians</Typography>
                        <Typography variant="h2" className="reveal" sx={{ color: BRAND.purpleDark, fontSize: { xs: '2rem', md: '3rem' }, lineHeight: 1.15, mb: 3 }}>
                            Know your credit.<br />
                            <Box component="em" sx={{ fontStyle: 'italic', color: BRAND.purple }}>Own your financial future.</Box>
                        </Typography>
                        <Typography className="reveal" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 5, fontSize: '1rem' }}>
                            Whether you're renting an apartment, leasing equipment, or simply want to understand your financial standing — NG Credit's consumer tool gives every Nigerian access to their credit profile, free from the opacity of traditional banking.
                        </Typography>
                        {CREDIT_FEATURES.map((f, i) => (
                            <Box key={i} className={`reveal d${i + 1}`} sx={{ display: 'flex', gap: 2.5, mb: 3.5 }}>
                                <Typography sx={{ fontSize: '.7rem', color: BRAND.purple, fontWeight: 700, letterSpacing: '.12em', mt: .5, flexShrink: 0 }}>{f.num}</Typography>
                                <Box>
                                    <Typography variant="h4" sx={{ fontSize: '1.05rem', color: BRAND.purpleDark, mb: .75, lineHeight: 1.2 }}>{f.title}</Typography>
                                    <Typography sx={{ color: 'text.secondary', fontSize: '.88rem', lineHeight: 1.75 }}>{f.desc}</Typography>
                                </Box>
                            </Box>
                        ))}
                        <Box className="reveal" sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                            <Button variant="contained" size="large" onClick={() => window.open('files/NG_Credit_Report.pdf')}
                                    sx={{ background: BRAND.purple, px: 4, py: 1.6, fontSize: '.92rem', '&:hover': { background: BRAND.purpleLight, transform: 'translateY(-2px)', boxShadow: `0 12px 32px ${alpha(BRAND.purple,.4)}` }, transition: 'all .3s' }}>
                                View Sample Report
                            </Button>
                        </Box>
                    </Box>
                    <Box className="reveal-right">
                        <Box sx={{ background: BRAND.purpleDeep, borderRadius: 5, p: { xs: 4, md: 4.5 }, boxShadow: `0 32px 80px ${alpha(BRAND.purpleDark,.28)}`, position: 'relative', overflow: 'hidden' }}>
                            <Box sx={{ position: 'absolute', top: -60, right: -60, width: 240, height: 240, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(BRAND.purpleLight,.2)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
                            <Typography sx={{ color: BRAND.accent, fontSize: '.65rem', letterSpacing: '.18em', textTransform: 'uppercase', fontWeight: 700, mb: .75 }}>NG Credit Score</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: .5 }}>
                                <Typography variant="h2" sx={{ color: '#fff', fontSize: '4.5rem', fontWeight: 400, lineHeight: 1 }}>653</Typography>
                                <Typography sx={{ color: '#4caf50', fontWeight: 700, fontSize: '.9rem' }}>▲ 60</Typography>
                            </Box>
                            <Typography sx={{ color: alpha('#fff',.4), fontSize: '.78rem', mb: 3.5 }}>Next update in 2 days</Typography>
                            {[
                                { label: 'Income Stability',   val: 85, grade: 'Good' },
                                { label: 'Recurring Payments', val: 90, grade: 'Excellent' },
                                { label: 'Expense Ratio',      val: 75, grade: 'Good' },
                                { label: 'Overdraft Behavior', val: 70, grade: 'Fair' },
                            ].map((item, i) => (
                                <Box key={i} sx={{ mb: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: .6 }}>
                                        <Typography sx={{ color: alpha('#fff',.7), fontSize: '.75rem' }}>{item.label}</Typography>
                                        <Typography sx={{ color: BRAND.accent, fontSize: '.72rem', fontWeight: 600 }}>{item.grade}</Typography>
                                    </Box>
                                    <Box sx={{ background: alpha('#fff',.1), borderRadius: 10, height: 6, overflow: 'hidden' }}>
                                        <Box sx={{ width: `${item.val}%`, height: '100%', background: `linear-gradient(90deg, ${BRAND.purpleLight}, ${BRAND.accent})`, borderRadius: 10 }} />
                                    </Box>
                                </Box>
                            ))}
                            <Box sx={{ mt: 3, pt: 2.5, borderTop: `1px solid ${alpha('#fff',.1)}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography sx={{ color: alpha('#fff',.4), fontSize: '.65rem', letterSpacing: '.12em', textTransform: 'uppercase' }}>Risk Level</Typography>
                                    <Typography sx={{ color: '#4caf50', fontWeight: 700, fontSize: '1.1rem', mt: .25 }}>LOW</Typography>
                                </Box>
                                <Box sx={{ background: alpha('#4caf50',.15), border: `1px solid ${alpha('#4caf50',.3)}`, borderRadius: 2, px: 2, py: .75 }}>
                                    <Typography sx={{ color: '#4caf50', fontSize: '.72rem', fontWeight: 700, letterSpacing: '.08em' }}>RENT READY ✓</Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
};

const CTA: React.FC = () => {
    const navigate = useNavigate();
    return (
        <Box id="apply" sx={{ background: `linear-gradient(135deg, ${BRAND.purple} 0%, ${BRAND.purpleLight} 100%)`, py: { xs: 12, md: 18 }, px: { xs: 3, md: 6 }, textAlign: 'center' }}>
            <Container maxWidth="sm">
                <Typography variant="h2" className="reveal" sx={{ color: '#fff', fontSize: { xs: '2.5rem', md: '4rem' }, lineHeight: 1.1, mb: 2.5 }}>
                    Ready to deploy<br />industrial credit?
                </Typography>
                <Typography className="reveal" sx={{ color: alpha('#fff',.65), fontSize: '1.05rem', mb: 4.5, lineHeight: 1.75 }}>
                    Join our growing network of SMEs building Nigeria's food, aquaculture, and circular materials future with NG Credit.
                </Typography>
                <Box className="reveal" sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <Button variant="contained" size="large" onClick={() => navigate('/apply')}
                            sx={{ background: '#fff', color: BRAND.purple, px: 5, py: 1.8, fontSize: '1rem', fontWeight: 800, '&:hover': { background: '#fff', transform: 'translateY(-3px)', boxShadow: `0 16px 44px ${alpha(BRAND.purpleDeep,.4)}` }, transition: 'all .3s' }}>
                        Apply for Credit
                    </Button>
                    <Button variant="outlined" size="large" onClick={() => navigate('/demo')}
                            sx={{ borderColor: 'rgba(255,255,255,.4)', color: '#fff', px: 5, py: 1.8, fontSize: '1rem', '&:hover': { borderColor: '#fff', background: 'rgba(255,255,255,.1)' } }}>
                        View Platform Demo
                    </Button>
                </Box>
            </Container>
        </Box>
    );
};

const Footer: React.FC = () => (
    <Box component="footer" sx={{ background: BRAND.ink, py: 5, px: { xs: 3, md: 6 }, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Logo size={30} />
            <Typography sx={{ fontFamily: "'Instrument Serif',serif", fontSize: '1.2rem', color: '#fff' }}>NG Credit</Typography>
        </Box>
        <Typography sx={{ color: alpha('#fff',.25), fontSize: '.78rem' }}>© {new Date().getFullYear()} NG Credit. Finance the future.</Typography>
        <Box sx={{ display: 'flex', gap: 3 }}>
            {([
                { label: 'Privacy', href: '/legal?tab=privacy' },
                { label: 'Terms',   href: '/legal?tab=terms'   },
                { label: 'Contact', href: 'mailto:corporate@ng-credit.com' },
            ] as const).map(({ label, href }) => (
                <Box key={label} component="a" href={href} sx={{ color: alpha('#fff',.35), fontSize: '.78rem', textDecoration: 'none', transition: 'color .2s', '&:hover': { color: BRAND.accent } }}>{label}</Box>
            ))}
        </Box>
    </Box>
);

const Landing: React.FC = () => {
    useReveal();
    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <AppGlobalStyles />
            <Box sx={{ overflowX: 'hidden', width: '100%', position: 'relative' }}>
                <Nav />
                <Hero />
                <Marquee />
                <CreditCheck />
                <About />
                <Architecture />
                <Roadmap />
                <RevenueModel />
                <Ecosystem />
                <CTA />
                <Footer />
            </Box>
        </ThemeProvider>
    );
};

export { Hero, Architecture, RevenueModel, Ecosystem, CreditCheck };
export default Landing;